import {PilotFeedbackQueue} from './pilot-feedback.js';
/**
 * TIP Shell — District Console (admin seat; trusted chrome; parent DOM only).
 *
 * The district evaluator's document of record: the apps in force across the
 * tenant and the exact grant each one holds, the roster-integrity report, the
 * tenant-wide audit ledger, and the hash-chain verdict. PURE presentation: every
 * decision (authz, the kill switch, restore) is the SERVER's; a deny or error
 * renders as a flat line, never a retry-with-different-params.
 *
 * Design (DESIGN.md, Paper & Seal): reads top-to-bottom like a legal instrument —
 * the apps and their grants, the roster it trusts, the record, the verdict. The
 * serif appears only in on-the-record sentences (the clean-roster attestation, the
 * suspend/restore confirmations, the chain verdict); seal-red only on the moments
 * that count — a suspended app, a broken trust assumption, a failed chain. Confirms
 * are INLINE progressive disclosure, never modals. Long surrogate uuids inside
 * findings are DISPLAY-truncated only (full text in the tooltip; the hash chain is
 * the authority, not this view).
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { SealMark, CAP_PHRASES, AccountControls, SiteFooter } from './ui.js';
import { TheRecord, compactIds } from './record.js';
import { SchoolsSection } from './schools.js';
import type {
  ServerClient, SessionInfo, AdminOverviewResult, AdminApp, AdminChain,
  RosterIntegrityReport, ClarifyEffectResult, ClarifyCohortStats,
} from './api.js';

interface AdminProps {
  api: ServerClient;
  session: SessionInfo;
  /** Top-level nav to the Teacher Console (?view=teach). */
  onTeacherView(): void;
  /** Top-level nav to the sealed app view (?view=app). */
  onAppView(): void;
}

export function DistrictConsole({ api, session, onTeacherView, onAppView }: AdminProps) {
  const [overview, setOverview] = useState<AdminOverviewResult | null>(null);
  const [audit, setAudit] = useState<string[] | null>(null);
  const [integrity, setIntegrity] = useState<RosterIntegrityReport | null>(null);
  const [clarify, setClarify] = useState<ClarifyEffectResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // one action at a time
  const [results, setResults] = useState<Record<string, string>>({}); // per-app outcome sentence
  const [confirm, setConfirm] = useState<Record<string, boolean>>({}); // per-app inline confirm open

  const refresh = useMemo(
    () => async () => {
      try {
        const [ov, au] = await Promise.all([
          api.adminOverview(),
          api.auditLines().then((a) => a.lines).catch(() => null),
        ]);
        setOverview(ov);
        setAudit(au);
        setError(null);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
      // Non-blocking: the roster report is advisory, never gates the page.
      api.rosterIntegrity().then(setIntegrity).catch(() => setIntegrity(null));
      // Likewise advisory: a product measurement must never be able to break the
      // district's own view of what is running.
      api.clarifyEffect().then(setClarify).catch(() => setClarify(null));
    },
    [api],
  );
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => { document.title = 'District Console · TIP'; }, []);

  const doSuspend = async (app: AdminApp) => {
    setBusy(`kill:${app.id}`);
    try {
      const r = await api.adminSuspend(app.id, 'district admin suspend');
      setResults((m) => ({ ...m, [app.id]: `Suspended. On the record at ${fmtTs(r.audit.ts)}.` }));
      setConfirm((m) => ({ ...m, [app.id]: false }));
    } catch (e) {
      setResults((m) => ({ ...m, [app.id]: `Could not: ${e instanceof Error ? e.message : String(e)}` }));
    }
    setBusy(null);
    void refresh();
  };

  const doRestore = async (app: AdminApp) => {
    setBusy(`restore:${app.id}`);
    try {
      const r = await api.adminRestore(app.id);
      // `enabled` is the actual transition — the chained event alone is
      // authorization, not outcome. A lost race or failed flip renders flat.
      setResults((m) => ({
        ...m,
        [app.id]: r.enabled
          ? `Restored. On the record at ${fmtTs(r.audit.ts)}.`
          : 'Could not: the restore was authorized on the record, but the app did not re-enable. Refresh and retry.',
      }));
      setConfirm((m) => ({ ...m, [app.id]: false }));
    } catch (e) {
      setResults((m) => ({ ...m, [app.id]: `Could not: ${e instanceof Error ? e.message : String(e)}` }));
    }
    setBusy(null);
    void refresh();
  };

  return (
    <div>
      <header class="top">
        <SealMark />
        <span class="wordmark">TIP</span>
        <span class="crumb">District Console</span>
        <span class="spacer" />
        <button type="button" class="quiet" onClick={onTeacherView}>Teacher view</button>
        <button type="button" class="quiet" onClick={onAppView}>App view</button>
        <AccountControls api={api} role={session.role} />
      </header>
      <hr class="hairline top-rule" />

      <main class="sheet-field">
        {error ? <div class="notice-line">The district overview could not load: {error}</div> : null}

        <div class="doc">
          <div>
            {/* A. Apps in force */}
            <section>
              <h2 class="section-title">Apps in force</h2>
              {overview === null ? (
                error ? null : <p style="color:var(--ink-faint);">Loading the district’s apps…</p>
              ) : overview.apps.length === 0 ? (
                <p style="color:var(--ink-soft);max-width:52ch;">
                  No apps are provisioned in this district yet. When one is, it appears here
                  with the exact grant it holds.
                </p>
              ) : (
                overview.apps.map((app) => (
                  <AppRow
                    key={app.id}
                    app={app}
                    busy={busy}
                    result={results[app.id]}
                    confirmOpen={confirm[app.id] ?? false}
                    setConfirmOpen={(open) => setConfirm((m) => ({ ...m, [app.id]: open }))}
                    doSuspend={doSuspend}
                    doRestore={doRestore}
                  />
                ))
              )}
            </section>

            {/* A2. Schools — how each class's apps look (migration 0016). */}
            <SchoolsSection api={api} onChanged={() => void refresh()} />

            <PilotFeedbackQueue api={api} />

            {/* B. Roster integrity */}
            <section class="section-gap">
              <h2 class="section-title">Roster integrity</h2>
              <RosterReport integrity={integrity} counts={overview?.counts ?? null} />
            </section>

            {/* B2. Does asking teachers what they forgot actually help? */}
            <section class="section-gap">
              <h2 class="section-title">Does the clarifying interview help?</h2>
              <ClarifyEffect data={clarify} />
            </section>
          </div>

          {/* C. The Record — tenant-wide, and this is the ONLY seat that renders it
                 (it left the teacher console 2026-07-25; see shell/record.tsx). */}
          <TheRecord audit={audit} busy={busy} onRefresh={() => void refresh()} />
        </div>

        {/* D. The chain verdict — the closing statement */}
        {overview ? <ChainFoot chain={overview.chain} /> : null}
      </main>
      <SiteFooter api={api} />
    </div>
  );
}

function AppRow(props: {
  app: AdminApp;
  busy: string | null;
  result: string | undefined;
  confirmOpen: boolean;
  setConfirmOpen(open: boolean): void;
  doSuspend(app: AdminApp): Promise<void>;
  doRestore(app: AdminApp): Promise<void>;
}) {
  const { app, busy, result, confirmOpen } = props;
  const acting = busy === `kill:${app.id}` || busy === `restore:${app.id}`;

  return (
    <div class="asg-row">
      <div class="row-top">
        <span class="title">{app.name}</span>
        {app.enabled ? (
          <span class="sub">In force</span>
        ) : (
          <span class="state-seal">Suspended</span>
        )}
      </div>

      <div class="grant-line">
        {app.manifestValid ? (
          grantSentence(app)
        ) : (
          <span class="state-seal">Manifest invalid. Unrunnable.</span>
        )}
      </div>

      {!confirmOpen ? (
        <div class="row-act">
          <span class="push">
            {/* Opening the confirmation is the step; CONFIRMING is the seal-red act
                below. The kill switch must be findable at a glance in a room with a
                CISO in it — a text link is not that. */}
            <button
              type="button"
              class={app.enabled ? 'step undo' : 'step'}
              disabled={busy !== null}
              onClick={() => props.setConfirmOpen(true)}
            >
              {app.enabled ? 'Suspend' : 'Restore'}
            </button>
          </span>
        </div>
      ) : (
        <div class="ledger-line settle" style="border-bottom:0;">
          <span class="record-voice" style="max-width:60ch;">
            {app.enabled ? (
              <>Suspend {app.name} for every class in the district? Students lose access immediately.</>
            ) : (
              <>Restore {app.name}? The kill switch lifts for every class.</>
            )}
          </span>
          <span class="push">
            <button
              type="button"
              class="act"
              style="padding:9px 16px;"
              disabled={busy !== null}
              onClick={() => void (app.enabled ? props.doSuspend(app) : props.doRestore(app))}
            >
              {acting
                ? app.enabled ? 'Suspending…' : 'Restoring…'
                : app.enabled ? 'Confirm suspend' : 'Confirm restore'}
            </button>
            <button
              type="button"
              class="quiet"
              disabled={busy !== null}
              onClick={() => props.setConfirmOpen(false)}
            >
              Cancel
            </button>
          </span>
        </div>
      )}

      {result ? (
        <div class="action-result settle" style="font-family:var(--sans);color:var(--ink-soft);font-size:14px;">
          {result}
        </div>
      ) : null}
    </div>
  );
}

function RosterReport({ integrity, counts }: {
  integrity: RosterIntegrityReport | null;
  counts: { classes: number; students: number; teachers: number } | null;
}) {
  if (integrity === null) {
    return <p style="color:var(--ink-faint);">The roster report is not readable right now.</p>;
  }

  const findings: string[] = [
    ...integrity.membersNotStudent.map(
      (m) => `A member of class ${m.classId} is recorded as ${m.role}, not a student: ${m.userId}.`,
    ),
    ...integrity.classesTaughtByStudents.map(
      (c) => `Class ${c.classId} is taught by a student account: ${c.teacherUserId}.`,
    ),
    ...integrity.studentsWithSuspectGrade.map(
      (s) => `Student ${s.userId} carries a grade level that fails validation: ${String(s.gradeLevel)}.`,
    ),
  ];

  return (
    <div>
      {integrity.ok && findings.length === 0 ? (
        <p class="record-voice">
          Roster attested clean.
          {counts
            ? ` ${counts.students} ${counts.students === 1 ? 'student' : 'students'} across ` +
              `${counts.classes} ${counts.classes === 1 ? 'class' : 'classes'}.`
            : ''}
        </p>
      ) : (
        findings.map((f, i) => (
          <div class="ledger-line" key={`v${i}`}>
            <span class="dot seal" />
            <span class="state-seal" title={f}>{compactIds(f)}</span>
          </div>
        ))
      )}
      {integrity.studentsMissingGrade.map((s, i) => {
        const w = `Student ${s.userId} has no grade level on file; the app runs name-blind for them until the grade is attested.`;
        return (
          <div class="ledger-line" key={`w${i}`} style="color:var(--ink-faint);font-size:13.5px;">
            <span title={w}>{compactIds(w)}</span>
          </div>
        );
      })}
    </div>
  );
}

function ChainFoot({ chain }: { chain: AdminChain }) {
  if (chain.ok) {
    return (
      <p class="record-voice" style="text-align:center;margin-top:56px;color:var(--ink-soft);">
        The record verifies: {chain.length} entries, hash chain intact.
      </p>
    );
  }
  return (
    <p class="record-voice" style="text-align:center;margin-top:56px;color:var(--seal);">
      ⚠ The record FAILS verification at entry {chain.brokenAt}: {chain.reason}
    </p>
  );
}

/** The manifest grant as one plain-English sentence a board attorney can read:
 *  "Standard tier. Writes scores, reads scores, displays first names." */
function grantSentence(app: AdminApp): string {
  const parts: string[] = [];
  if (app.requestedTier === 'standard') parts.push('Standard tier.');
  else if (app.requestedTier === 'high_assurance') parts.push('High-assurance tier.');

  if (app.capabilities.length > 0) {
    const clause = app.capabilities.map((c) => CAP_PHRASES[c] ?? c.replace(/_/g, ' ')).join(', ');
    parts.push(`${clause.charAt(0).toUpperCase()}${clause.slice(1)}.`);
  }
  if (app.requestedFields.length > 0) {
    parts.push(`Fields requested: ${app.requestedFields.join(', ')}.`);
  }
  return parts.length > 0 ? parts.join(' ') : 'No tier or capabilities requested.';
}

/** Best-effort human timestamp for the on-the-record outcome line; falls back to raw. */
function fmtTs(ts: string): string {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

/**
 * "Does the clarifying interview help?" — the product measurement, in the district seat.
 *
 * DESIGN NOTE, because it is the whole reason this reads the way it does: the temptation
 * with a measurement panel is to show a big confident number. This one deliberately leads
 * with the SENTENCES the server generated — including "not enough yet to compare" — and
 * puts the table underneath as supporting detail. The server refuses to state a comparison
 * below its small-n floor, and the UI must not undo that refusal by rendering a table that
 * invites the reader to eyeball a difference the data cannot support. The caveat line is
 * rendered with the same weight as the findings, not as fine print.
 */
function ClarifyEffect({ data }: { data: ClarifyEffectResult | null }) {
  if (data === null) {
    return (
      <p style="color:var(--ink-faint);max-width:56ch;">
        Not available. (This is a product measurement, not a district control — the rest of
        this page is unaffected.)
      </p>
    );
  }
  if (data.totalLineages === 0) {
    return (
      <p style="color:var(--ink-soft);max-width:56ch;">
        No activities have been built in this district yet. Once teachers start building,
        this compares those who used the clarifying interview with those who did not.
      </p>
    );
  }

  const label: Record<ClarifyCohortStats['cohort'], string> = {
    one_shot: 'Went straight to building',
    clarified: 'Answered the questions',
    asked_unused: 'Opened it, answered nothing',
  };

  return (
    <div>
      {data.readings.map((line) => (
        <p key={line} style="max-width:60ch;margin:0 0 .5rem 0;">{line}</p>
      ))}

      <div style="overflow-x:auto;">
        <table style="border-collapse:collapse;font-size:.9rem;margin-top:.75rem;min-width:34rem;">
          <thead>
            <tr style="text-align:left;color:var(--ink-soft);">
              <th style="padding:.25rem .75rem .25rem 0;font-weight:600;">Teachers who…</th>
              <th style="padding:.25rem .75rem;font-weight:600;">Activities</th>
              <th style="padding:.25rem .75rem;font-weight:600;">Published</th>
              <th style="padding:.25rem .75rem;font-weight:600;">Built, never published</th>
              <th style="padding:.25rem .75rem;font-weight:600;">Avg. revisions</th>
              <th style="padding:.25rem 0 .25rem .75rem;font-weight:600;">First run worked</th>
            </tr>
          </thead>
          <tbody>
            {data.cohorts.map((c) => {
              const ran = c.firstRun.clean + c.firstRun.withErrors + c.firstRun.blank + c.firstRun.unreachable;
              return (
                <tr key={c.cohort} style="border-top:1px solid var(--rule);">
                  <td style="padding:.4rem .75rem .4rem 0;">{label[c.cohort]}</td>
                  <td style="padding:.4rem .75rem;">{c.lineages}</td>
                  <td style="padding:.4rem .75rem;">{c.published}</td>
                  <td style="padding:.4rem .75rem;">{c.abandoned}</td>
                  {/* An em dash, never a 0: "withheld" and "zero revisions needed" are
                      opposite findings and must not share a glyph. */}
                  <td style="padding:.4rem .75rem;">
                    {c.avgRefinesBeforePublish === null ? '—' : c.avgRefinesBeforePublish}
                  </td>
                  <td style="padding:.4rem 0 .4rem .75rem;">
                    {ran === 0 ? '—' : `${c.firstRun.clean} of ${ran}`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style="color:var(--ink-faint);max-width:60ch;margin-top:.6rem;font-size:.9rem;">
        One “activity” is one teacher’s attempt, counted once however many times they
        revised it. “Built, never published” counts activities that passed every safety
        gate and were then left unpublished. An em dash means there is not enough data to
        say.
      </p>
    </div>
  );
}
